import { Box, Link as ChakraLink, Text } from '@chakra-ui/react';
import {
  NavigationHeading,
  NavigationItem as NavigationItemType,
  NavigationLink,
  NavigationSection as NavigationSectionType,
  NavigationTextConfig
} from '@lib/docs/types';
import Link from 'next/link';
import { SidenavItem } from '../layout/sidenav-item';
import { NavigationSection } from './nav-section';
import { determineNavigationItemType } from '@lib/docs/nav';
import { detectExternalLink, makeDocsLink } from '@lib/docs/utils';
import { ICON_MAP } from './icons';

export function NavigationItem({
  item,
  activePath
}: {
  item: NavigationItemType;
  activePath: string;
}) {
  const type = determineNavigationItemType(item);

  switch (type) {
    case 'separator': {
      return <Box as="hr" my={2} />;
    }
    case 'heading': {
      const heading = item as NavigationHeading;

      const text = (
        typeof heading === 'string' ? heading : heading.title
      ).replace('# ', '');

      // Heading
      return (
        <Text
          as="span"
          key={text}
          color="var(--primaryTextColor)"
          textStyle="md-medium"
          pb={1}
          pt={4}
          px={4}
          display="inline-block"
        >
          {text}
        </Text>
      );
    }
    case 'link': {
      const [textConfig, rest] = item as NavigationLink;

      let href;
      let isExternal;
      let isActive;

      if (detectExternalLink(rest)) {
        href = rest;
        isExternal = true;
      } else {
        isExternal = false;
        href = makeDocsLink(rest);
        isActive = href === activePath;
      }

      const text =
        typeof textConfig === 'string' ? textConfig : textConfig.title;
      const icon =
        typeof textConfig === 'string' || !textConfig?.icon
          ? undefined
          : ICON_MAP[textConfig.icon];
      const tag = typeof textConfig === 'string' ? textConfig : textConfig?.tag;
      return (
        <ChakraLink
          as={Link}
          key={href}
          href={href}
          isExternal={isExternal}
          display="block"
        >
          <SidenavItem
            icon={icon}
            isActive={isActive}
            isExternal={isExternal}
            text={text}
            size="sm"
            tag={tag}
          />
        </ChakraLink>
      );
    }
    case 'section': {
      return (
        <NavigationSection
          config={item as NavigationSectionType}
          activePath={activePath}
        />
      );
    }
    case 'text': {
      const text =
        typeof item === 'string' ? item : (item as NavigationTextConfig).title;

      return <SidenavItem isActive={false} text={text} />;
    }
    default: {
      console.warn('[nav]', 'Unknown navigation item type', item);
      return null;
    }
  }
}
